(function () {
  'use strict';

  // Self-hosted html-to-image. Preferred source: the data attribute the
  // section templates set from Liquid (carries the correct ?v= cache
  // buster for THIS file, not ours). Fallback: derive from our own src
  // minus its query string.
  const ownScript = document.querySelector('script[src*="fml-event-share"]');
  const HTML_TO_IMAGE_URL =
    (ownScript && ownScript.dataset.htmlToImageSrc) ||
    (ownScript && ownScript.src.split('?')[0].replace(/fml-event-share\.js/, 'fml-html-to-image.js'));
  let htmlToImagePromise = null;

  function loadHtmlToImage() {
    if (window.htmlToImage) return Promise.resolve(window.htmlToImage);
    if (htmlToImagePromise) return htmlToImagePromise;
    htmlToImagePromise = new Promise(function (resolve, reject) {
      const s = document.createElement('script');
      s.src = HTML_TO_IMAGE_URL;
      s.async = true;
      s.crossOrigin = 'anonymous';
      s.onload = function () { resolve(window.htmlToImage); };
      s.onerror = function () { reject(new Error('html-to-image load failed')); };
      document.head.appendChild(s);
    });
    return htmlToImagePromise;
  }

  function showToast(message) {
    let toast = document.querySelector('.fml-share-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'fml-share-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('fml-share-toast--visible');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function () {
      toast.classList.remove('fml-share-toast--visible');
    }, 2200);
  }

  function setBtnBusy(btn, busy) {
    if (busy) {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      btn.dataset._originalLabel = btn.querySelector('.fml-share-btn__label')?.textContent || '';
      const label = btn.querySelector('.fml-share-btn__label');
      if (label) label.textContent = 'Se pregătește...';
    } else {
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
      const label = btn.querySelector('.fml-share-btn__label');
      if (label && btn.dataset._originalLabel) label.textContent = btn.dataset._originalLabel;
    }
  }

  async function fetchAsFile(url, filename) {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const ext = (blob.type.split('/')[1] || 'jpg').replace('jpeg', 'jpg');
    return new File([blob], filename + '.' + ext, { type: blob.type });
  }

  async function rasterizeNode(node, filename) {
    const lib = await loadHtmlToImage();
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch (_) {}
    }
    const blob = await lib.toBlob(node, {
      pixelRatio: 2,
      cacheBust: true,
      backgroundColor: '#FBF8EE',
      style: { transform: 'none' }
    });
    if (!blob) throw new Error('rasterize returned empty');
    return new File([blob], filename, { type: 'image/png' });
  }

  function findMediaColForBtn(btn) {
    return btn.closest('.fml-event-card-wrap, .fml-event-hero__media-col');
  }

  function findMediaForBtn(btn) {
    const col = findMediaColForBtn(btn);
    if (!col) return null;
    return col.querySelector('.fml-event-card__media, .fml-event-hero__media');
  }

  async function buildShareFiles(btn) {
    const files = [];
    const col = findMediaColForBtn(btn);

    // Hero carousel: rasterize all hidden slides (cover + text chunks)
    const slides = col ? col.querySelectorAll('[data-fml-slide]') : null;
    if (slides && slides.length > 0) {
      let idx = 1;
      for (const slide of slides) {
        try {
          files.push(await rasterizeNode(slide, `flow-music-lab-event-${idx}.png`));
        } catch (err) {
          console.warn('[fml-share] slide rasterize failed', err);
        }
        idx++;
      }
      if (files.length > 0) return files;
    }

    // Cards: uploaded image
    const imgUrl = btn.dataset.shareImage;
    if (imgUrl) {
      try {
        files.push(await fetchAsFile(imgUrl, 'flow-music-lab-event'));
        return files;
      } catch (_) {}
    }

    // Cards without uploaded image: rasterize on-page cover
    const mediaEl = findMediaForBtn(btn);
    if (mediaEl) {
      try {
        files.push(await rasterizeNode(mediaEl, 'flow-music-lab-event.png'));
        return files;
      } catch (err) {
        console.warn('[fml-share] media rasterize failed', err);
      }
    }
    return files;
  }

  async function copyToClipboard(url, copiedMsg, failedMsg) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        showToast(copiedMsg);
        return;
      } catch (_) {}
    }
    const ta = document.createElement('textarea');
    ta.value = url;
    ta.setAttribute('readonly', '');
    ta.style.position = 'absolute';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand('copy');
      showToast(copiedMsg);
    } catch (_) {
      showToast(failedMsg);
    }
    document.body.removeChild(ta);
  }

  async function handleShare(btn) {
    const url = btn.dataset.shareUrl;
    const title = btn.dataset.shareTitle || document.title;
    const text = btn.dataset.shareText || '';
    const copiedMsg = btn.dataset.toastCopied || 'Link copiat!';
    const failedMsg = btn.dataset.toastFailed || 'Nu am putut copia. Selectează manual.';

    if (!navigator.share) {
      await copyToClipboard(url, copiedMsg, failedMsg);
      return;
    }

    setBtnBusy(btn, true);
    let files = [];
    try {
      files = await buildShareFiles(btn);
    } catch (_) {}
    setBtnBusy(btn, false);

    const payload = { url, title, text };
    if (files.length > 0 && navigator.canShare && navigator.canShare({ files })) {
      payload.files = files;
    } else if (files.length > 0 && navigator.canShare && navigator.canShare({ files: [files[0]] })) {
      payload.files = [files[0]];
    }

    try {
      await navigator.share(payload);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      await copyToClipboard(url, copiedMsg, failedMsg);
    }
  }

  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-fml-share]');
    if (!btn) return;
    e.preventDefault();
    handleShare(btn);
  });
})();
